import React, { useMemo, useState } from 'react';
import { TagIcon, ClockIcon, ArrowRightIcon, BarChartIcon, PencilIcon, MegaphoneIcon, UsersIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
// Resource types with category
interface Resource {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
  category: string;
  bgColor: string;
  duration: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  author: string;
  authorAvatar: string;
  resourceType: 'Selling' | 'Setup' | 'Marketing' | 'Design' | 'Tutorials';
}
// Only real guides with article pages
const resources: Resource[] = [{
  id: 1,
  title: 'How to Price Your Digital Products',
  description: 'Learn how to choose the right pricing strategy based on value, competition, and target audience to maximize sales without undervaluing your work.',
  icon: <BarChartIcon className="h-6 w-6" data-id="element-1098" />,
  link: '/learn/pricing-strategies',
  category: 'Guide',
  bgColor: 'bg-blue-50',
  duration: '15 min',
  difficulty: 'Intermediate',
  author: 'Alex Morgan',
  authorAvatar: 'https://randomuser.me/api/portraits/men/32.jpg',
  resourceType: 'Selling'
}, {
  id: 2,
  title: 'Writing Product Listings That Convert',
  description: 'Discover how to create clear, compelling product titles and descriptions that attract buyers and increase conversions.',
  icon: <PencilIcon className="h-6 w-6" data-id="element-1099" />,
  link: '/learn/product-descriptions',
  category: 'Tutorial',
  bgColor: 'bg-green-50',
  duration: '12 min',
  difficulty: 'Beginner',
  author: 'Sarah Wilson',
  authorAvatar: 'https://randomuser.me/api/portraits/women/44.jpg',
  resourceType: 'Selling'
}, {
  id: 3,
  title: 'How to Market Your Digital Products Effectively',
  description: 'Learn practical strategies to promote your products using social media, content, and targeted outreach to reach the right audience and drive consistent sales.',
  icon: <MegaphoneIcon className="h-6 w-6" data-id="element-1100" />,
  link: '/learn/marketing-strategy',
  category: 'Guide',
  bgColor: 'bg-orange-50',
  duration: '14 min',
  difficulty: 'Intermediate',
  author: 'Chris Taylor',
  authorAvatar: 'https://randomuser.me/api/portraits/men/72.jpg',
  resourceType: 'Marketing'
}, {
  id: 4,
  title: 'Building an Audience Before You Launch',
  description: 'Discover how to grow an engaged audience before releasing your product so you can launch with momentum and generate immediate interest and sales.',
  icon: <UsersIcon className="h-6 w-6" data-id="element-1101" />,
  link: '/learn/building-audience',
  category: 'Strategy',
  bgColor: 'bg-purple-50',
  duration: '16 min',
  difficulty: 'Beginner',
  author: 'Lisa Johnson',
  authorAvatar: 'https://randomuser.me/api/portraits/women/35.jpg',
  resourceType: 'Marketing'
}];
// Filter categories
const categories = ['All', 'Selling', 'Setup', 'Marketing', 'Design', 'Tutorials'];
const ResourceCard = ({
  resource
}: {
  resource: Resource;
}) => {
  return <motion.div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300" whileHover={{
    y: -5,
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  }} data-id="element-1102">
      <div className={`${resource.bgColor} p-6`} data-id="element-1103">
        <div className="flex justify-between items-start" data-id="element-1104">
          <div className="text-[#2DB489]" data-id="element-1105">{resource.icon}</div>
          <div className="flex items-center space-x-1" data-id="element-1106">
            <ClockIcon className="h-3.5 w-3.5 text-slate-400" data-id="element-1107" />
            <span className="text-xs text-slate-500" data-id="element-1108">{resource.duration}</span>
          </div>
        </div>
        <h3 className="text-xl font-semibold mt-4 text-[#0F3B46]" data-id="element-1109">
          {resource.title}
        </h3>
        <p className="mt-2 text-[#1F2F35]" data-id="element-1110">{resource.description}</p>
        <div className="mt-4 flex items-center justify-between" data-id="element-1111">
          <div className="flex items-center" data-id="element-1112">
            <img src={resource.authorAvatar} alt={resource.author} className="w-6 h-6 rounded-full object-cover mr-2" data-id="element-1113" />
            <span className="text-xs text-slate-500" data-id="element-1114">{resource.author}</span>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full ${resource.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' : resource.difficulty === 'Intermediate' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-1115">
            {resource.difficulty}
          </span>
        </div>
      </div>
      <div className="px-6 py-4 bg-white flex justify-between items-center" data-id="element-1116">
        <span className="text-sm font-medium text-slate-400 flex items-center" data-id="element-1117">
          <TagIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-1118" />
          {resource.category}
        </span>
        <Link to={resource.link} className="text-[#2DB489] dark:text-[#4ED1A0] font-medium hover:underline flex items-center" data-id="element-1119">
          Learn More
          <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-1120" />
        </Link>
      </div>
    </motion.div>;
};
// Abstract glow component
const AbstractGlow = ({
  className,
  color
}: {
  className: string;
  color: string;
}) => {
  return <div className={`absolute ${className}`} data-id="element-1121">
      <div className={`w-full h-full rounded-full ${color} blur-3xl opacity-20 blob-animation`} data-id="element-1122"></div>
    </div>;
};
export const LearnGrow = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const filteredResources = useMemo(() => {
    if (activeFilter === 'All') return resources;
    return resources.filter(r => r.resourceType === activeFilter);
  }, [activeFilter]);
  return <section id="resources" className="py-20 bg-white dark:bg-[#0F1F1D] relative overflow-hidden transition-colors duration-300" data-id="element-1123">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-1124">
        {/* Abstract glow shapes */}
        <AbstractGlow className="top-[10%] right-[5%] w-[400px] h-[400px]" color="bg-[#2DB489] dark:bg-[#4ED1A0]" data-id="element-1125" />
        <AbstractGlow className="bottom-[15%] left-[10%] w-[300px] h-[300px]" color="bg-[#0D3C45] dark:bg-[#0B3C45]" data-id="element-1126" />
        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmZmZmYwNSIgZD0iTTM2IDM0aDR2MWgtNHYtMXptMC0yaDF2NGgtMXYtNHptMi0yaDF2MWgtMXYtMXptLTIgMmgxdjFoLTF2LTF6bS0yLTJoMXYxaC0xdi0xem0yLTJoMXYxaC0xdi0xem0tMiAyaDF2MWgtMXYtMXptLTItMmgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bS0yIDRoMXYxaC0xdi0xem0yIDBoeDF2MWgtMXYtMXptLTIgMmgxdjFoLTF2LTF6bTQtNGgxdjFoLTF2LTF6Ii8+PC9nPjwvc3ZnPg==')] opacity-20" data-id="element-1127"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1128">
        {/* Hero Block */}
        <div className="bg-gradient-to-r from-[#0D3C45]/10 to-[#2DB489]/10 dark:from-[#0D3C45]/20 dark:to-[#2DB489]/20 rounded-2xl p-8 md:p-12 mb-16 text-center relative overflow-hidden" data-id="element-1129">
          <motion.h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#0F3B46] dark:text-[#F8FAF9]" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-1130">
            Master selling your digital products
          </motion.h2>
          <motion.p className="text-xl text-[#1F2F35] dark:text-[#B0C1BD] max-w-3xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-1131">
            Learn, grow, and earn more with our comprehensive resources and
            community support.
          </motion.p>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-1132">
          {categories.map(category => <motion.button key={category} onClick={() => setActiveFilter(category)} className={`px-6 py-2 rounded-full text-sm font-medium transition-all relative ${activeFilter === category ? 'bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white' : 'bg-gray-100 dark:bg-[#162825] text-[#1F2F35] dark:text-[#B0C1BD] hover:bg-gray-200 dark:hover:bg-[#1A3330]'}`} whileHover={{
          scale: 1.03,
          boxShadow: activeFilter === category ? '0 4px 12px rgba(45, 180, 137, 0.2)' : '0 4px 6px rgba(0, 0, 0, 0.05)'
        }} whileTap={{
          scale: 0.95
        }} data-id="element-1133">
              {category}
            </motion.button>)}
        </div>

        {/* Resources grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" style={{
        minHeight: '200px'
      }} data-id="element-1134">
          {filteredResources.length > 0 ? filteredResources.map(resource => <div key={resource.id} data-id="element-1135">
                <ResourceCard resource={resource} data-id="element-1136" />
              </div>) : <div className="col-span-full text-center py-12" data-id="element-1137">
              <p className="text-gray-400 dark:text-[#7A8F8A] text-lg" data-id="element-1138">
                New guides coming soon for this category.
              </p>
            </div>}
        </div>

        {/* Newsletter signup */}
        <div className="mt-20 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-2xl p-8 md:p-12 max-w-6xl mx-auto relative overflow-hidden" data-id="element-1139">
          {/* Background decoration */}
          <div className="absolute -right-24 -top-24 w-64 h-64 rounded-full bg-white opacity-10" data-id="element-1140"></div>
          <div className="absolute -left-12 -bottom-12 w-40 h-40 rounded-full bg-white opacity-10" data-id="element-1141"></div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 relative z-10" data-id="element-1142">
            <div className="text-white md:max-w-md" data-id="element-1143">
              <h3 className="text-2xl font-bold mb-2" data-id="element-1144">Stay Updated</h3>
              <p className="opacity-90" data-id="element-1145">
                Subscribe to our newsletter to receive the latest updates,
                tutorials, and exclusive offers.
              </p>
            </div>
            <div className="w-full md:w-auto" data-id="element-1146">
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-1147">
                <input type="email" placeholder="Enter your email" className="px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 min-w-[250px]" data-id="element-1148" />
                <motion.button className="bg-white text-[#0F3B46] dark:text-[#2DB489] px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-all whitespace-nowrap" whileHover={{
                scale: 1.03,
                boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)'
              }} whileTap={{
                scale: 0.95
              }} data-id="element-1149">
                  Subscribe
                </motion.button>
              </div>
              <p className="text-white text-sm opacity-80 mt-2" data-id="element-1150">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
};